import React, { useState, useMemo, useEffect } from 'react';
import {
  Box,
  TextField,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Typography,
  Menu,
  MenuItem,
  Chip,
  Button,
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Visibility as VisibilityIcon,
  ShoppingBag as ShoppingBagIcon,
  AssignmentReturn as AssignmentReturnIcon,
  Edit as EditIcon,
  CalendarToday as CalendarIcon,
  Cached as DevolverIcon,
  CompareArrows as UnificarIcon,
  MoreVert as MoreVertIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { ptBR } from 'date-fns/locale';
import { format } from 'date-fns';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { saidaService, Saida, retornoService } from '../../services/saida';
import SelectPraca from '../praca/SelectPraca';
import SelectVendedor from '../vendedor/SelectVendedor';
import SelectFuncionario from '../funcionario/SelectFuncionario';

const SaidaTable: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedSaida, setSelectedSaida] = useState<Saida | null>(null);
  
  // Filtros
  const [dataSaidaInicio, setDataSaidaInicio] = useState<Date | undefined>(undefined);
  const [dataSaidaFim, setDataSaidaFim] = useState<Date | undefined>(undefined);
  const [dataRetornoInicio, setDataRetornoInicio] = useState<Date | undefined>(undefined);
  const [dataRetornoFim, setDataRetornoFim] = useState<Date | undefined>(undefined);
  const [idPraca, setIdPraca] = useState<number>(0);
  const [idVendedor, setIdVendedor] = useState<number>(0);
  const [idPromotor, setIdPromotor] = useState<number>(0);
  const [page, setPage] = useState(0);
  const rowsPerPage = 25;

  // Definir data padrão dos últimos 6 meses
  useEffect(() => {
    const hoje = new Date();
    const seisMesesAtras = new Date();
    seisMesesAtras.setMonth(hoje.getMonth() - 6);
    
    setDataSaidaInicio(seisMesesAtras);
    setDataSaidaFim(hoje);
  }, []);

  // Resetar página ao mudar filtros
  const resetPage = () => setPage(0);

  // Ao mudar o promotor, limpar praça e vendedor (filtros dependentes)
  const handlePromotorChange = (value: number) => {
    setIdPromotor(value);
    setIdPraca(0);
    setIdVendedor(0);
    resetPage();
  };

  // Ao mudar a praça, limpar vendedor (filtro dependente)
  const handlePracaChange = (value: number) => {
    setIdPraca(value);
    setIdVendedor(0);
    resetPage();
  };

  // Buscar saídas com filtros
  const { data: saidas = [], isLoading, error, refetch } = useQuery({
    queryKey: ['saidas', dataSaidaInicio, dataSaidaFim, dataRetornoInicio, dataRetornoFim, idPraca, idVendedor, idPromotor],
    queryFn: () => saidaService.getAllComFiltro({
      dataSaidaInicio: dataSaidaInicio ? format(dataSaidaInicio, 'yyyy-MM-dd') : undefined,
      dataSaidaFim: dataSaidaFim ? format(dataSaidaFim, 'yyyy-MM-dd') : undefined,
      dataRetornoInicio: dataRetornoInicio ? format(dataRetornoInicio, 'yyyy-MM-dd') : undefined,
      dataRetornoFim: dataRetornoFim ? format(dataRetornoFim, 'yyyy-MM-dd') : undefined,
      idPraca: idPraca || undefined,
      idVendedor: idVendedor || undefined,
      idPromotor: idPromotor || undefined,
    }),
  });

  // Filtrar saídas baseado na busca
  const filteredSaidas = useMemo(() => {
    if (!search.trim()) {
      return saidas;
    }

    const searchUpper = search.toUpperCase();
    return saidas.filter((saida) => {
      if (saida.vendedor?.toString().toUpperCase().includes(searchUpper)) {
        return true;
      }
      if (saida.id?.toString().includes(search)) {
        return true;
      }
      if (saida.idRetorno !== null && saida.idRetorno?.toString().includes(search)) {
        return true;
      }
      if (saida.praca?.toString().toUpperCase().includes(searchUpper)) {
        return true;
      }
      if (saida.dataSaidaString?.toString().includes(search)) {
        return true;
      }
      if (saida.dataPrevistaRetornoString?.toString().includes(search)) {
        return true;
      }
      return false;
    });
  }, [saidas, search]);

  const paginatedSaidas = useMemo(
    () => filteredSaidas.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [filteredSaidas, page]
  );

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>, saida: Saida) => {
    setAnchorEl(event.currentTarget);
    setSelectedSaida(saida);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedSaida(null);
  };

  const handleCadastro = () => {
    navigate('/saidas/novo');
  };

  const handleVisualizar = (id: number) => {
    navigate(`/saidas/visualizar/${id}`);
    handleMenuClose();
  };

  const handleVisualizarRetorno = (idRetorno: number) => {
    navigate(`/saidas/retorno/visualizar/${idRetorno}`);
    handleMenuClose();
  };

  const handleRetorno = (id: number) => {
    navigate(`/saidas/retorno/${id}`);
    handleMenuClose();
  };

  const handleAdicionarProdutos = (id: number) => {
    navigate(`/saidas/adicionar-produtos/${id}`);
    handleMenuClose();
  };

  const handleAlterarDatas = (id: number) => {
    navigate(`/saidas/alterar-datas/${id}`);
    handleMenuClose();
  };

  const handleDevolverItens = async (id: number) => {
    try {
      await retornoService.devolverTodosItens(id);
      refetch();
      handleMenuClose();
    } catch (error) {
      console.error('Erro ao devolver itens:', error);
    }
  };

  const handleUnificar = (id: number) => {
    navigate(`/saidas/unificar/${id}`);
    handleMenuClose();
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography>Carregando...</Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <Typography color="error">Erro ao carregar as saídas.</Typography>
      </Box>
    );
  }

  return (
    <Paper sx={{ width: '100%', overflow: 'hidden' }}>
      {/* Filtros */}
      <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1, minWidth: '200px' }}>
            <SelectFuncionario
              value={idPromotor}
              onChange={handlePromotorChange}
              label="Promotor"
              fullWidth={true}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: '200px' }}>
            <SelectPraca
              value={idPraca}
              onChange={handlePracaChange}
              label="Praça"
              fullWidth={true}
              idFuncionario={idPromotor || undefined}
            />
          </Box>
          <Box sx={{ flex: 1, minWidth: '200px' }}>
            <SelectVendedor
              value={idVendedor}
              onChange={setIdVendedor}
              label="Cliente"
              fullWidth={true}
              idPraca={idPraca || undefined}
            />
          </Box>
        </Box>
        
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={ptBR}>
            <DatePicker
              label="Data Saída Início"
              value={dataSaidaInicio}
              onChange={(newValue) => setDataSaidaInicio(newValue || undefined)}
              slotProps={{
                textField: {
                  sx: { minWidth: '14%' },
                },
              }}
            />
            <DatePicker
              label="Data Saída Fim"
              value={dataSaidaFim}
              onChange={(newValue) => setDataSaidaFim(newValue || undefined)}
              minDate={dataSaidaInicio}
              slotProps={{
                textField: {
                  sx: { minWidth: '14%' },
                },
              }}
            />
            <DatePicker
              label="Data Retorno Início"
              value={dataRetornoInicio}
              onChange={(newValue) => setDataRetornoInicio(newValue || undefined)}
              slotProps={{
                textField: {
                  sx: { minWidth: '14%' },
                },
              }}
            />
            <DatePicker
              label="Data Retorno Fim"
              value={dataRetornoFim}
              onChange={(newValue) => setDataRetornoFim(newValue || undefined)}
              minDate={dataRetornoInicio}
              slotProps={{
                textField: {
                  sx: { minWidth: '14%' },
                },
              }}
            />
          </LocalizationProvider>
        </Box>

        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 2 }}>
          <TextField
            placeholder="Buscar nos resultados..."
            variant="outlined"
            size="small"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 300, flex: 1 }}
            slotProps={{
              input: {
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />,
              },
            }}
          />

          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleCadastro}
            sx={{ backgroundColor: 'primary.main', '&:hover': { backgroundColor: 'primary.dark' } }}
          >
            Nova Saída
          </Button>
        </Box>
      </Box>

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell sx={{ fontWeight: 'bold' }}>Código</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Retorno</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Praça</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Cliente</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Data de Saída</TableCell>
              <TableCell sx={{ fontWeight: 'bold' }}>Data Retorno</TableCell>
              <TableCell align="right" sx={{ fontWeight: 'bold' }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredSaidas.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} align="center">
                  <Typography sx={{ py: 4, color: 'text.secondary' }}>
                    {search ? 'Nenhuma saída encontrada para o filtro informado.' : 'Nenhuma saída cadastrada.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              paginatedSaidas.map((saida) => (
                <TableRow key={saida.id} hover>
                  <TableCell>{saida.id}</TableCell>
                  <TableCell>
                    {saida.idRetorno ? (
                      <Chip label={saida.idRetorno} color="success" size="small" />
                    ) : (
                      <Chip label="Sem retorno" color="warning" size="small" />
                    )}
                  </TableCell>
                  <TableCell>{saida.praca}</TableCell>
                  <TableCell>{saida.vendedor}</TableCell>
                  <TableCell>{saida.dataSaidaString}</TableCell>
                  <TableCell>{saida.dataPrevistaRetornoString}</TableCell>
                  <TableCell align="right">
                    <IconButton
                      onClick={(e) => handleMenuClick(e, saida)}
                      size="small"
                    >
                      <MoreVertIcon />
                    </IconButton>

                    <Menu
                      anchorEl={anchorEl}
                      open={Boolean(anchorEl) && selectedSaida?.id === saida.id}
                      onClose={handleMenuClose}
                    >
                      <MenuItem onClick={() => handleVisualizar(saida.id)}>
                        <VisibilityIcon sx={{ mr: 1, fontSize: 20 }} />
                        Visualizar
                      </MenuItem>
                      {saida.idRetorno ? (
                        <MenuItem onClick={() => handleVisualizarRetorno(saida.idRetorno!)}>
                          <ShoppingBagIcon sx={{ mr: 1, fontSize: 20 }} />
                          Visualizar Retorno
                        </MenuItem>
                      ) : (
                        <MenuItem onClick={() => handleRetorno(saida.id)}>
                          <AssignmentReturnIcon sx={{ mr: 1, fontSize: 20 }} />
                          Adicionar Retorno
                        </MenuItem>
                      )}
                      {saida.idRetorno == null && (
                        <MenuItem onClick={() => handleAdicionarProdutos(saida.id)}>
                          <EditIcon sx={{ mr: 1, fontSize: 20 }} />
                          Adicionar Produtos
                        </MenuItem>
                      )}
                      {saida.idRetorno == null && (
                        <MenuItem onClick={() => handleAlterarDatas(saida.id)}>
                          <CalendarIcon sx={{ mr: 1, fontSize: 20 }} />
                          Alterar Datas
                        </MenuItem>
                      )}
                      {saida.idRetorno == null && (
                        <MenuItem onClick={() => handleDevolverItens(saida.id)}>
                          <DevolverIcon sx={{ mr: 1, fontSize: 20 }} />
                          Devolver Todos os Itens
                        </MenuItem>
                      )}
                      {saida.idRetorno == null && (
                        <MenuItem onClick={() => handleUnificar(saida.id)}>
                          <UnificarIcon sx={{ mr: 1, fontSize: 20 }} />
                          Unificar
                        </MenuItem>
                      )}
                    </Menu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredSaidas.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={rowsPerPage}
        rowsPerPageOptions={[25]}
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
      />
    </Paper>
  );
};

export default SaidaTable;
